import React from 'react';

import { Typography } from '@material-ui/core';
import { get } from 'lodash';
import { Appointment } from 'requests/resources/appointment';

import { PatientFormTemplateItem } from '@lib/resources/types';

import { ChatContextProps } from '../../contexts/ChatContext';
import MessageScheduler from '../Scheduler/MessageScheduler';

import ChatMessage, { ChatMessageProps } from './ChatMessage';
import ManageAppointments from './ManageAppointments';
import Message2FA from './Message2FA';
import MessageBookStatus from './MessageBookStatus';
import MessageMultipleChoice from './MessageMultipleChoice';

export type TMessageWrapper = ChatMessageProps['message'] & {
  metadata: {
    item?: PatientFormTemplateItem;
    flowTemplateId?: string;
    flowId?: string;
    patient?: string;
    answer?: any;
    includeRevert?: boolean;
    isLastMessage?: boolean;
    forceInput?: boolean;
    action?: 'send2FACode' | 'verifyPatient' | 'scheduleAppointment';
    hideAvatar?: boolean;
    hideName?: boolean;
    appointment?: Appointment;
    nextMessage?: any;
    prevMessage?: any;
  };
};

export interface MessageWrapperProps extends Omit<ChatMessageProps, 'classes'> {
  message: TMessageWrapper;
  onSubmit: ChatContextProps['pushAnswer'];
}

const MessageWrapper: React.FC<MessageWrapperProps> = ({ message, variant, onSubmit, onRevert, ...rest }) => {
  const {
    metadata: { item, appointment, prevMessage, nextMessage },
  } = message;
  const itemType = get(item, 'type');
  const isInbound = message.type === 'inbound';

  if (isInbound) {
    // Multiple choice
    if (itemType === 'multiple_choice' || itemType === 'yes_no') {
      return (
        <MessageMultipleChoice
          nextMessage={nextMessage}
          prevMessage={prevMessage}
          variant={variant}
          message={message}
          onSubmit={onSubmit}
          {...rest}
        />
      );
    }
    // 2FA
    if (itemType === '2FA') {
      return (
        <Message2FA
          nextMessage={nextMessage}
          prevMessage={prevMessage}
          variant={variant}
          message={message}
          onSubmit={onSubmit}
          {...rest}
        />
      );
    }

    if (itemType === 'book_status_component' && appointment) {
      return <MessageBookStatus appointment={appointment} />;
    }

    if (itemType === 'manageAppointment') {
      const currentDate = new Date();
      return (
        <ManageAppointments
          nextMessage={nextMessage}
          prevMessage={prevMessage}
          variant={variant}
          message={message}
          onSubmit={onSubmit}
          currentDate={currentDate}
          {...rest}
        />
      );
    }
  }

  const template = get(item, 'template') || get(item, 'properties.template');

  if (itemType === 'scheduler') {
    return (
      <MessageScheduler
        nextMessage={nextMessage}
        prevMessage={prevMessage}
        message={message}
        onSubmit={onSubmit}
        messageTemplate={template as string}
        variant={variant}
        onRevert={onRevert}
        {...rest}
      />
    );
  }

  return (
    <ChatMessage
      nextMessage={nextMessage}
      prevMessage={prevMessage}
      variant={variant}
      message={message}
      onRevert={onRevert}
      {...rest}
    >
      {template && <Typography dangerouslySetInnerHTML={{ __html: template }} />}
    </ChatMessage>
  );
};

export default MessageWrapper;
